import React, { FC, useEffect, useMemo } from 'react';
import { Button, Form, Input, Modal, Switch, Tooltip } from 'antd';
import { SelectWithSearch } from 'common/components';
import { renderRequiredMark, rules } from 'common/helpers';
import { useAppSelector } from 'app/store/store.hooks';
import { AddressSuggestionSelector } from 'entities/Address/components';
import { IAddressSuggestion } from 'entities/Address/Address.models';
import { ICity, IUpdateCity } from 'entities/City/City.models';
import { selectCityError, selectCityParents } from 'entities/City/City.slice';

interface IComponentProps {
  open: boolean;
  loading: boolean;
  city: ICity | null;
  onCancel: () => void;
  onSubmit: (payload: IUpdateCity) => void;
}

export const CreateCityModal: FC<IComponentProps> = ({ open, loading, city, onCancel, onSubmit }) => {
  // city
  const cityError = useAppSelector(selectCityError);
  const cityParents = useAppSelector(selectCityParents);

  const [form] = Form.useForm();
  const parentOptions = useMemo(() => cityParents.map((i) => ({ label: i.name, value: i.id })), [cityParents]);

  useEffect(() => {
    if (!city) {
      return;
    }

    form.setFieldsValue({
      name: city.name,
      id: city.id,
      region: city.region,
      parentCityId: city.parent?.id,
      isAvailable: city.isAvailable,
    });
  }, [city]);

  useEffect(() => {
    form.setFields([{ name: 'name', errors: cityError ? [cityError] : [] }]);
  }, [cityError]);

  const selectAddress = (suggestion: IAddressSuggestion) => {
    form.setFieldsValue({
      name: suggestion.city,
      id: suggestion.cityId,
      region: suggestion.region,
    });

    if (!suggestion.cityId) {
      form.setFields([{ name: 'name', errors: ['Продолжайте ввод — нужно выбрать конкретный адрес.'] }]);
    }
  };

  const handleCancel = (): void => {
    if (loading) {
      return;
    }

    form.resetFields();
    onCancel();
  };

  const handleSubmit = (values: IUpdateCity): void => {
    if (!values.id) {
      form.setFields([{ name: 'name', errors: ['Продолжайте ввод — нужно выбрать конкретный адрес.'] }]);
      return;
    }

    onSubmit({ ...values, onSuccess: handleCancel });
  };

  return (
    <Modal
      rootClassName="redesign modal"
      title="Настроить город"
      styles={{ header: { marginTop: 0 } }}
      width={400}
      centered
      open={open}
      mask={false}
      destroyOnClose
      onCancel={handleCancel}
      footer={[
        <Button key="cancel" className="button-sm secondary" onClick={handleCancel} loading={loading}>
          Отмена
        </Button>,
        <Button key="submit" className="button-sm primary" onClick={form.submit} loading={loading}>
          Сохранить
        </Button>,
      ]}
    >
      <Form
        form={form}
        layout="vertical"
        requiredMark={renderRequiredMark}
        onFinish={handleSubmit}
        onValuesChange={(changedValues) => {
          if ('name' in changedValues) {
            form.setFieldValue('region', undefined);
          }
        }}
      >
        <Form.Item name="name" label="Название города" rules={[rules.required()]}>
          <AddressSuggestionSelector placeholder="Начните вводить название города" onSelect={selectAddress} />
        </Form.Item>
        <Form.Item name="id" hidden>
          <Input />
        </Form.Item>
        <Form.Item name="region" hidden>
          <Input />
        </Form.Item>
        <Form.Item
          className="mb-6"
          name="parentCityId"
          label="Родительский город (для сателлита)"
          getValueFromEvent={(value) => value ?? null}
        >
          <SelectWithSearch placeholder="Выберите из списка" options={parentOptions} />
        </Form.Item>
        <div className="mb-6 flex align-center justify-between">
          <span className="ff-regular fs-s">Доступен покупателям</span>
          <Tooltip title={city?.locked ? 'Город используется в складе — его нельзя скрыть от покупателей' : ''}>
            <Form.Item className="mb-0" name="isAvailable" valuePropName="checked" initialValue={true}>
              <Switch disabled={city?.locked} />
            </Form.Item>
          </Tooltip>
        </div>
      </Form>
    </Modal>
  );
};
